import React from 'react';

import { Field, FormikProps } from 'formik';

interface CustomInputProps {
	/** Formik field name */
	name: string;

	/** HTML input type */
	type?: React.HTMLInputTypeAttribute;

	/** Placeholder text */
	placeholder?: string;

	/** Label displayed above input */
	label?: string;

	/** Formik bag for accessing errors, touched and values */
	formikBag?: FormikProps<any>;

	/** Wrapper class name */
	classNameWrap?: string;

	/** Input class name */
	classNameInput?: string;
	/** Element or component to render instead of default input (e.g. "textarea") */
	component?: React.ElementType;

	/** Icon displayed inside input */
	innerIcon?: string | JSX.Element;

	/** Trim value (remove spaces at start and end) */
	trimValue?: boolean;

	/** Remove ALL spaces from value */
	removeSpacesAll?: boolean;

	/** HTML autocomplete attribute */
	autocomplete?: string;

	/** Max input length */
	maxLength?: number;

	/** Called on blur with current value */
	onBlur?: (val: string) => void;

	/** Called when value changes (without Formik handling) */
	onChange?: (val: string) => void;

	/** Additional external onChange handler (with Formik handling) */
	handleOnChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

	/** onFocus handler */
	onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const CustomInput: React.FC<CustomInputProps> = ({
	type = 'text',
	name,
	label = '',
	placeholder = '',
	classNameWrap = 'custom-input',
	classNameInput = '',
	component,
	formikBag,
	innerIcon = null,
	trimValue = false,
	removeSpacesAll = false,
	autocomplete = 'off',
	maxLength,
	handleOnChange,
	onBlur,
	onChange,
	onFocus,
}) => {
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);

	React.useEffect(() => {
		if (component === 'textarea' && maxLength && maxLength > 0 && textareaRef.current) {
			const handleInput = () => {
				const textarea = textareaRef.current;

				if (textarea) {
					textarea.style.height = 'auto';
					textarea.style.height = `${textarea.scrollHeight}px`;
				}
			};
			handleInput();
			textareaRef?.current?.addEventListener('input', handleInput);

			return () => {
				textareaRef?.current?.removeEventListener('input', handleInput);
			};
		}
	}, [component]);

	return (
		<div
			className={`${classNameWrap} ${
				formikBag && formikBag.errors?.[name] && formikBag.touched?.[name] ? 'error' : ''
			} ${
				formikBag && (typeof formikBag.values?.[name] === 'number' || formikBag.values?.[name]?.length) ? 'focused' : ''
			}`}
		>
			{label && <label htmlFor={name}>{label}</label>}
			<Field
				innerRef={textareaRef}
				as={component}
				type={type}
				name={name}
				placeholder={placeholder}
				id={name}
				maxLength={maxLength}
				autoComplete={autocomplete}
				className={classNameInput}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					if (onChange) {
						onChange(e.target.value);
					} else {
						formikBag && formikBag.setFieldValue(
							name,
							trimValue ? e.target.value.trim() : removeSpacesAll ? e.target.value.replace(' ', '') : e.target.value,
						);
						handleOnChange && handleOnChange(e);
					}
				}}
				onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
					formikBag && formikBag.setFieldTouched(name, true);
					onBlur && onBlur(e.target.value);
				}}
				onFocus={onFocus}
			/>
			<div className="custom-input__inner-icon">{innerIcon}</div>
			{formikBag && formikBag.errors[name] && formikBag.touched[name] && (
				<div className="custom-input__error">
					{formikBag.errors[name]}
				</div>
			)}
			<div className="custom-input__placeholder-top">{placeholder}</div>
		</div>
	);
};

export default CustomInput;
